<template>
	<div class="request-form">
		<transition
			name="scale-show"
			mode="out-in"
		>
			<div :key="success ? 1 : 0">
				<form
					class="form"
					@submit.prevent="validateForm()"
					v-show="!success"
				>
					<div class="form__title">
						{{ $t('leaveRequest') }}
					</div>

					<div class="row">
						<div class="col-12 col-md">
							<label
								class="form__label"
								for="reqform_phone"
								>{{ $t('form.enterPhone') }}:
								<span class="d-md-none">*</span></label
							>
							<div class="required-input">
								<the-mask
									id="reqform_phone'"
									v-model="formData.phone"
									type="tel"
									:mask="phoneMask[$i18n.locale]"
									:placeholder="phonePlaceholder[$i18n.locale]"
									:masked="true"
									:required="false"
									:class="{ error: errors.phone }"
									@input="inputPhone($event)"
								></the-mask>
							</div>
							<div
								class="input-errors"
								v-if="errors.phone"
							>
								<div
									class="input-errors__item"
									v-for="(err, i) in errors.phone"
									:key="i"
								>
									{{ err }}
								</div>
							</div>
						</div>
						<div class="col-12 col-md">
							<label
								class="form__label"
								for="reqform_name"
								>{{ $t('form.enterName') }}:</label
							>
							<the-mask
								id="reqform_name"
								v-model="formData.name"
								:tokens="nameToken"
								mask="NNNNNNNNNNNNNNNNNNNNNNNNNNNNNN"
								max-length="30"
								:class="{ error: errors.name }"
								@input="removeError('name')"
							></the-mask>
							<div
								class="input-errors"
								v-if="errors.name"
							>
								<div
									class="input-errors__item"
									v-for="(err, i) in errors.name"
									:key="i"
								>
									{{ err }}
								</div>
							</div>
						</div>
						<div class="col-12 col-xl-auto">
							<div class="inputfile">
								<div class="d-flex flex-column">
									<label class="form__label">
										<div class="row d-md-none">
											<div class="col-auto pr-0">
												<i class="icon-attach"></i>
											</div>
											<div class="col pl-2">
												{{ $t('form.attachFileTextMobile') }}
											</div>
										</div>
										<div class="d-none d-md-block">
											{{ $t('form.attachFileText') }}:
										</div>
									</label>
									<label
										for="reqform_files"
										class="btn-outlined has-icon"
									>
										{{ $t('form.attachFile') }}
										<i class="icon-attach"></i>
									</label>
									<input
										id="reqform_files"
										type="file"
										name="files[]"
										multiple="true"
										@change="setFiles"
										@input="removeError('files')"
									/>
								</div>
								<div class="inputfile__result">
									<i
										v-if="formData.files.length"
										class="icon-attach"
									></i>
									{{ filesLabel }}
								</div>
								<div
									class="input-errors"
									v-if="errors.files"
								>
									<div
										v-for="(err, i) in errors.files"
										:key="i"
									>
										{{ err }}
									</div>
								</div>
							</div>
						</div>
					</div>

					<label
						class="form__label"
						for="reqform_comment"
						>{{ $t('form.enterComment') }}:</label
					>
					<textarea
						id="reqform_comment"
						v-model="formData.comment"
						:class="{ error: errors.comment }"
						@input="removeError('comment')"
					></textarea>
					<div
						class="input-errors"
						v-if="errors.comment"
					>
						<div
							v-for="(err, i) in errors.comment"
							:key="i"
						>
							{{ err }}
						</div>
					</div>

					<PrivacyAgreement
						v-model="formData.privacyAgreement"
						:errors="errors.privacyAgreement"
						@input="removeError('privacyAgreement')"
					/>

					<div class="request-form__bottom row">
						<div class="col-12 col-md-auto">
							<div class="custom-captcha-badge">
								<div>
									{{ $t('form.recaptchaText') }}
									<a
										class="link-light"
										href="https://policies.google.com/privacy"
										target="_blank"
										>{{ $t('privacyPolicy') }}</a
									>
									{{ $t('and') }}
									<a
										class="link-light"
										href="https://policies.google.com/terms"
										target="_blank"
										>{{ $t('userAgreement') }}</a
									>.
								</div>
							</div>
						</div>
						<div class="col-12 col-md-auto">
							<button
								class="request-form__submit-btn btn-primary"
								:class="{ loading: sending }"
								type="submit"
							>
								<i class="gradient"></i>
								{{ sending ? $t('form.sending') : $t('form.send') }}
							</button>
						</div>
					</div>

					<div
						class="input-errors text-right mt-1 mb-0 d-none d-md-block"
						v-if="errors.main"
					>
						<div
							class="input-errors__item"
							v-for="(err, i) in errors.main"
							:key="i"
						>
							<div class="error-chip">{{ err }}</div>
						</div>
					</div>
					<div
						class="input-errors d-md-none mt-4"
						v-if="errors.main"
					>
						<div class="divider"></div>
						<div class="mb-1">{{ $t('form.occurredErrors') }}:</div>
						<div
							v-for="(err, i) in errors.main"
							:key="i"
						>
							- {{ err }}
						</div>
					</div>
				</form>

				<div
					class="form-success"
					v-show="success"
				>
					<div class="form-success__title">{{ $t('thanks') }}!</div>
					<div class="form-success__text">
						{{ $t('form.successfullySent') }}!
					</div>
					<button
						class="btn-primary small d-none d-md-inline-flex"
						@click="success = false"
					>
						<i class="gradient"></i> {{ $t('excellent') }}!
					</button>
				</div>
			</div>
		</transition>

		<vue-recaptcha
			ref="recaptcha"
			size="invisible"
			:sitekey="sitekey"
			@verify="submitForm"
			@expired="onCaptchaExpired"
		/>
	</div>
</template>

<script>
import Popup from '@/components/Popup'
import PrivacyAgreement from '@/components/form/PrivacyAgreement.vue'
import VueRecaptcha from 'vue-recaptcha'
import { TheMask } from 'vue-the-mask'
import forEach from 'lodash-es/forEach'

export default {
	components: {
		Popup,
		PrivacyAgreement,
		VueRecaptcha,
		TheMask,
	},

	data() {
		return {
			formData: {
				name: '',
				phone: '',
				comment: '',
				privacyAgreement: false,
				files: [],
			},
			nameToken: {
				N: { pattern: /[а-яА-Яa-zA-Z ]/ },
			},
			phoneMask: {
				ru: '+7 (###) ###-##-##',
				en: '###############',
			},
			phonePlaceholder: {
				ru: '+7 (___) ___-__-__',
				en: '',
			},
			sending: false,
			success: false,
			sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
		}
	},

	watch: {
		'$i18n.locale'() {
			// При смене языка очищаем форму:
			this.removeError()
			this.formData = {
				name: '',
				phone: '',
				comment: '',
				files: [],
			}
		},
	},

	computed: {
		filesLabel() {
			let files = this.formData.files
			if (!files.length) return ''
			else if (files.length < 2) return files[0].name
			else return this.$t('form.choosedFiles', { amount: files.length })
		},

		errors() {
			return this.$store.state.general.requestFormErrors
		},
	},

	methods: {
		validateForm() {
			if (this.sending) return
			let formValid = true
			let phoneMinLength = 0
			if (this.$i18n.locale == 'ru')
				phoneMinLength = this.phoneMask['ru'].length
			else if (this.$i18n.locale == 'en') phoneMinLength = 9
			if (this.formData.phone.length < phoneMinLength) {
				this.$store.dispatch('general/setRequestFormErrors', {
					field: 'phone',
					fieldErrors: [this.$t('form.phoneFormatError')],
				})
				formValid = false
			}
			if (!this.formData.privacyAgreement) {
				this.$store.dispatch('general/setRequestFormErrors', {
					field: 'privacyAgreement',
					fieldErrors: [this.$t('form.privacyAgreement.error')],
				})
				formValid = false
			}
			if (!formValid) return
			// Запускаем капчу:
			this.$refs.recaptcha.execute()
		},

		submitForm(recaptchaToken) {
			this.formData.recaptchaToken = recaptchaToken
			this.sending = true
			this.$store
				.dispatch('general/sendRequestForm', this.formData)
				.then(() => {
					this.success = true
					this.formData = {
						name: '',
						phone: '',
						comment: '',
						files: [],
					}
					fbq('track', 'Lead')
				})
				.finally(() => {
					this.sending = false
				})
		},

		onCaptchaExpired() {
			this.$refs.recaptcha.reset()
		},

		setFiles(e) {
			this.formData.files = []
			this.removeError('files')
			forEach(e.target.files, (file) => this.formData.files.push(file))
			if (this.formData.files.length > 5) {
				this.formData.files = []
				this.$store.dispatch('general/setRequestFormErrors', {
					field: 'files',
					fieldErrors: [this.$t('form.maxFilesError', { maxFiles: 5 })],
				})
			}
		},

		removeError(field) {
			this.$store.dispatch('general/setRequestFormErrors', {
				field,
				fieldErrors: null,
			})
			this.$store.dispatch('general/setRequestFormErrors', {
				field: 'main',
				fieldErrors: null,
			})
		},

		inputPhone(val) {
			// Подменяем 8 на +7 для избежания опечаток юзера:
			if (val === '8') this.formData.phone = '+7'
			this.removeError('phone')
		},
	},
}
</script>

<style lang="scss" scoped>
@import '@/sass/_variables';

.request-form {
	padding: 52px;
	padding-bottom: 74px;
	background: #fff no-repeat url('../assets/request-form__bg.svg') center
		bottom / 100% auto;

	color: #333;
	border-radius: 10px;
	box-shadow: 0px 20px 40px 0px rgba(0, 24, 236, 0.2);

	&__bottom {
		justify-content: flex-start;
		align-items: center;
		margin-top: 10px;
	}

	&__submit-btn {
		width: 272px;
	}
}

.form-success {
	text-align: center;
	padding: 80px 0;

	&__title {
		font-size: 30px;
		font-weight: 800;
		margin-bottom: 10px;
	}

	&__text {
		font-size: 18px;
		margin-bottom: 30px;
	}
}

.custom-captcha-badge {
	max-width: 100%;
	border: 1px solid #e0e0e0;
	box-sizing: border-box;
	box-shadow: 0px 5px 30px rgba(0, 0, 0, 0.07);
	border-radius: 3px;
	width: 296px;
	min-height: 56px;
	font-size: 11px;
	padding: 4px 46px 4px 10px;
	background-color: #ffffff;
	background-image: url('../assets/recaptcha-logo.png');
	background-repeat: no-repeat;
	background-position: calc(100% - 5px) 50%;
	display: flex;
	align-items: center;
}

.scale-show-enter,
.scale-show-leave-active {
	opacity: 0;
	transform: scale(0.5);
}
.scale-show-enter-active,
.scale-show-leave-active {
	transition: 0.3s;
}

@media (max-width: $md) {
	.request-form {
		padding: 36px 16px 62px;
		background-size: auto;

		&__bottom {
			justify-content: space-between;
			margin-top: 8px;
		}

		&__submit-btn {
			width: 100%;
		}
	}

	.inputfile label.btn-outlined {
		height: 44px;
		width: 100%;
		padding: 12px 22px;

		i {
			display: none;
		}
	}

	.custom-captcha-badge {
		width: 100%;
		border-radius: 14px;
		margin-bottom: 24px;
	}
}
</style>
